/**
 * Per-table permissions management page.
 *
 * Standard Larasoft sub-page layout (per patterns-ui skill):
 *   - Back link
 *   - Header (title + subtitle)
 *   - Content panel (rounded-3xl)
 *     - Tabs (Users, Table, Areas, Versions)
 *     - Each tab renders its own search/controls + grid + pagination
 */

import { useEffect, useMemo, useState } from "react";
import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Loader2, Shield } from "lucide-react";

import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { PermissionDenied } from "@/components/ui/permission-denied";

import { useAuth } from "@/contexts/AuthContext";
import { useMyAppPermissions } from "@/hooks/useMyAppPermissions";
import { myappApi } from "@/services/api/myapp";
import type { FormDetail } from "@/types/myapp";

import { AreasTab } from "@/components/portal/tables/permissions/AreasTab";
import { TableTab } from "@/components/portal/tables/permissions/TableTab";
import { UsersTab } from "@/components/portal/tables/permissions/UsersTab";
import { VersionsTab } from "@/components/portal/tables/permissions/VersionsTab";

export const Route = createFileRoute("/portal/myapp/$tableId/permissions")({
  component: TablePermissionsPage,
});

type TabValue = "users" | "table" | "areas" | "versions";

function TablePermissionsPage() {
  const { tableId } = Route.useParams();
  const { token } = useAuth();

  const {
    permissions: objPerms,
    loading: permsLoading,
    denied: permissionDenied,
  } = useMyAppPermissions(tableId);

  const [table, setTable] = useState<FormDetail | null>(null);
  const [loadingTable, setLoadingTable] = useState(true);
  const [activeTab, setActiveTab] = useState<TabValue>("users");

  useEffect(() => {
    if (!token) return;
    let cancelled = false;
    setLoadingTable(true);
    myappApi.tables
      .get(token, tableId)
      .then((t) => {
        if (!cancelled) setTable(t);
      })
      .catch(() => {
        if (!cancelled) setTable(null);
      })
      .finally(() => {
        if (!cancelled) setLoadingTable(false);
      });
    return () => {
      cancelled = true;
    };
  }, [token, tableId]);

  const canManage = !!objPerms?.is_owner;

  const areaEnabled = useMemo(
    () => !!table && table.area_mode !== "none",
    [table],
  );
  const versionEnabled = useMemo(
    () => !!table && table.version_mode !== "filename",
    [table],
  );

  if (permsLoading || loadingTable) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (permissionDenied || !table) {
    return (
      <PermissionDenied message="You don't have permission to view this table's permissions." />
    );
  }

  return (
    <div className="space-y-6">
      {/* Back link */}
      <div className="flex items-center gap-4">
        <Link
          to="/portal/myapp/tables"
          className="flex items-center gap-2 text-sm text-brand-blue-medium transition hover:text-brand-blue-dark"
        >
          <ArrowLeft className="h-4 w-4" />
          Back to tables
        </Link>
      </div>

      {/* Header */}
      <div className="mt-4 flex items-start justify-between gap-4">
        <div>
          <div className="flex items-center gap-2">
            <Shield className="h-6 w-6 text-slate-500" />
            <h1 className="text-4xl font-bold text-slate-900">Permissions</h1>
          </div>
          <p className="mt-2 text-lg text-slate-600">
            Manage who can do what on{" "}
            <Link
              to="/portal/myapp/$tableId/grid"
              params={{ tableId }}
              search={{ mode: "create", ids: "" }}
              className="underline-offset-2 hover:underline"
            >
              {table.name}
            </Link>
            .
          </p>
          <p className="mt-2 text-sm text-slate-500">
            Access comes from either a <strong>direct user role</strong> or a{" "}
            <strong>group role</strong>. Group grants can cover the whole
            table, or narrower scopes (Areas / Versions). Each user's
            effective role on a row is the <em>max</em> of every grant that
            applies to them.
          </p>
        </div>
      </div>

      {!canManage && (
        <div className="rounded-md border border-amber-300 bg-amber-50 text-amber-900 px-4 py-2 text-sm">
          You're viewing permissions in read-only mode. Only owners of this
          table can change grants.
        </div>
      )}

      {/* Content panel */}
      <div className="rounded-3xl border border-slate-100 bg-white p-6 shadow-sm">
        <Tabs
          value={activeTab}
          onValueChange={(v) => setActiveTab(v as TabValue)}
        >
          <TabsList>
            <TabsTrigger value="users">Users</TabsTrigger>
            <TabsTrigger value="table">Groups — whole table</TabsTrigger>
            {areaEnabled && (
              <TabsTrigger value="areas">Groups — Areas</TabsTrigger>
            )}
            {versionEnabled && (
              <TabsTrigger value="versions">Groups — Versions</TabsTrigger>
            )}
          </TabsList>

          <TabsContent value="users" className="mt-6">
            <UsersTab tableId={tableId} canEdit={canManage} />
          </TabsContent>

          <TabsContent value="table" className="mt-6">
            <TableTab
              tableId={tableId}
              tableName={table.name}
              canEdit={canManage}
            />
          </TabsContent>

          {areaEnabled && (
            <TabsContent value="areas" className="mt-6">
              <AreasTab
                tableId={tableId}
                areaMode={
                  table.area_mode === "areas_and_subareas"
                    ? "areas_and_subareas"
                    : "areas_only"
                }
                canEdit={canManage}
              />
            </TabsContent>
          )}

          {versionEnabled && (
            <TabsContent value="versions" className="mt-6">
              <VersionsTab
                tableId={tableId}
                versionMode={
                  table.version_mode === "version_and_milestone"
                    ? "version_and_milestone"
                    : "version_only"
                }
                canEdit={canManage}
              />
            </TabsContent>
          )}
        </Tabs>
      </div>
    </div>
  );
}
